// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import { get, without } from 'lodash/fp';
import { connect } from 'react-redux';
import { compose, withStateHandlers } from 'recompose';
import t from 'app/utils/translate';
import { ACTIVE } from 'app/constants/lineupStatus';
import * as myTeamsActions from 'app/actions/myTeamsActions';
import SelectedEntityRow from 'app/components/challenge/SelectedEntityRow';
import GroupedChallengesList from 'app/components/challenges/GroupedChallengesList';
import Button from 'app/components/commons/Button';
import Loader from 'app/components/commons/Loader';
import SvgButton from 'app/components/commons/SvgButton';
import Modal from 'app/components/commons/Modal';
import Title from 'app/components/commons/Title';
import Responsive from 'app/components/responsive/Responsive';
import editLineUpSelector from 'app/selectors/editLineUpSelector';
import myTeamsSelector from 'app/selectors/myTeamsSelector';
import MyTeamsContent from './MyTeamsContent';
import ConfirmModal from './ConfirmModal';

type Props = {
  activeSport?: string,
  challengesData: Array<Object>,
  deleteLineups: query => void,
  onEditLineup: (lineupId: number) => void,
  loadLineupChallenges: (lineupId: number, query: Object) => void,
  isDeleting: boolean,
  isLineupChallengesLoading: boolean,
  selectedLineUp?: Object,
  lineups: Array<Object>,
  selectedIds: Array<string>,
  setSelectedIds: (ids: Array<string>) => void,
  setSelectionHash: (hash: string) => void,
  selectedSelectionHash: ?string
};

type State = {
  confirmDeleteLinups?: boolean,
  confirmDeleteLineupId?: number,
  confirmDeleteText?: string,
  goToChallenge: string
};

const FlexButtonContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.5em'
});

const StyledButton = styled(Button)(
  {
    minWidth: '7rem',
    textAlign: 'center'
  },
  ({ theme }) => ({
    margin: theme.spacing.xsmall,
    marginBottom: theme.spacing.large
  })
);

const ButtonsContainer = styled.div({
  display: 'flex'
});

const ButtonItem = styled.div({
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1
});

const EditIcon = styled(({ className }) => <span className={`${className} ion-edit`} />)({
  paddingRight: '0.2em'
});

const RemoveButton = styled(SvgButton)(({ theme }) => ({
  width: '34px',
  backgroundColor: theme.color.basis3,
  color: theme.color.white
}));

class MyUpcomingTeamsContent extends React.Component<Props, State> {
  props: Props;

  state: State = {
    confirmDeleteLinups: false,
    confirmDeleteLineupId: undefined,
    confirmDeleteText: undefined,
    goToChallenge: undefined
  };

  static defaultProps = {
    activeSport: undefined,
    selectedLineUp: undefined
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const {
      isLineupChallengesLoading,
      loadLineupChallenges,
      setSelectedIds,
      selectedSelectionHash
    } = this.props;

    if (
      nextProps.selectedLineUp &&
      nextProps.selectedSelectionHash &&
      nextProps.selectedSelectionHash !== selectedSelectionHash
    ) {
      loadLineupChallenges(nextProps.selectedLineUp.id, { status: ACTIVE });
    }

    if (
      isLineupChallengesLoading &&
      !nextProps.isLineupChallengesLoading &&
      Array.isArray(get('selectedLineUp.entryIds', nextProps)) &&
      get('selectedLineUp.firstChallenge.id', nextProps)
    ) {
      setSelectedIds(nextProps.selectedLineUp.entryIds);
      this.setState({ goToChallenge: get('selectedLineUp.firstChallenge.id', nextProps) });
    }
  }

  handleCheckBoxClick = id => {
    const { selectedIds, setSelectedIds, selectedLineUp } = this.props;
    const val = selectedIds.includes(id) ? without([id], selectedIds) : [...selectedIds, id];
    setSelectedIds(val);

    if (val.length > 0) {
      const challenge = selectedLineUp.challenges.find(({ myEntries }) =>
        myEntries.find(entry => entry.id === val[0])
      );

      if (challenge) {
        this.setState({ goToChallenge: challenge.id });
      }
    }
  };

  handleCancelDelete = () =>
    this.setState({
      confirmDeleteLineupId: undefined,
      confirmDeleteLinups: false
    });

  handleConfirmDelete = (id?: number) => {
    if (id) {
      this.setState({
        confirmDeleteLineupId: id,
        confirmDeleteLinups: false,
        confirmDeleteText: 'Delete this lineup?'
      });
    } else {
      this.setState({
        confirmDeleteLineupId: undefined,
        confirmDeleteLinups: true,
        confirmDeleteText: 'Delete all lineups with no entries?'
      });
    }
  };

  handleDelete = () => {
    const { deleteLineups } = this.props;
    const { confirmDeleteLineupId, confirmDeleteLinups } = this.state;

    if (confirmDeleteLinups) {
      deleteLineups();
    } else if (confirmDeleteLineupId) {
      deleteLineups({ id: confirmDeleteLineupId });
    }
    this.setState({ confirmDeleteLineupId: undefined, confirmDeleteLinups: false });
  };

  toggleSelectionModal(selectionHash, lineupId) {
    const { lineups, onEditLineup, setSelectionHash, selectedSelectionHash } = this.props;

    const lineup = lineups.find(l => l.id === lineupId);

    if (lineup && lineup.entryCount === 0) {
      onEditLineup(lineupId);
      return;
    }

    setSelectionHash(selectedSelectionHash === selectionHash ? null : selectionHash);
  }

  render() {
    const {
      activeSport,
      challengesData,
      isDeleting,
      isLineupChallengesLoading,
      lineups,
      selectedIds,
      selectedLineUp,
      setSelectionHash
    } = this.props;

    const {
      confirmDeleteLineupId,
      confirmDeleteLinups,
      confirmDeleteText,
      goToChallenge
    } = this.state;

    return (
      <div>
        <MyTeamsContent
          activeSport={activeSport}
          lineups={lineups}
          onConfirmDelete={() => this.handleConfirmDelete()}
          status={ACTIVE}
          renderEntity={entity => (
            <SelectedEntityRow
              key={entity.idStr}
              awayTeam={entity.awayAbbr}
              bonus={entity.bonus}
              disabled={entity.disabled}
              gameOrder={entity.gameOrder}
              highlight={entity.highlight}
              showButtons={false}
              homeTeam={entity.homeAbbr}
              id={entity.id}
              idStr={entity.idStr}
              image={entity.imageURI}
              initials={entity.initials}
              isHome={entity.isHome}
              isRacing={entity.isRacing}
              isScratched={entity.isScratched}
              labels={entity.labels}
              locked={entity.locked}
              name={entity.name}
              number={entity.number}
              place={entity.placeProjected}
              positionId={entity.positionId}
              positionLabel={entity.positionAbbr || entity.positionId}
              reverseOrder={entity.reverseOrder}
              rider={entity.rider}
              riderImage={entity.riderImage}
              salary={entity.salary}
              trainer={entity.trainer}
              win={entity.winProjected}
            />
          )}
          renderBottomButtons={(selectionHash, lineupId, entryCount) => (
            <Responsive desktop>
              {() => (
                <ButtonsContainer>
                  <ButtonItem>
                    <Button
                      size="small"
                      kind="secondary"
                      onClick={() => {
                        this.toggleSelectionModal(selectionHash, lineupId);
                      }}
                      block
                      autoBorderRadius={false}
                    >
                      <EditIcon /> Edit
                    </Button>
                  </ButtonItem>
                  <ButtonItem>
                    <Button
                      size="small"
                      kind="info"
                      link={`/enter-additional-challenges/${lineupId}`}
                      block
                      autoBorderRadius={false}
                    >
                      {t('Join More')}
                    </Button>
                  </ButtonItem>
                  {entryCount === 0 && (
                    <RemoveButton
                      type="icon-bin"
                      onClick={() => this.handleConfirmDelete(lineupId)}
                    />
                  )}
                </ButtonsContainer>
              )}
            </Responsive>
          )}
        />
        {selectedLineUp && (
          <Modal
            isOpen
            size="large"
            onCancel={() => {
              setSelectionHash(null);
            }}
          >
            {isLineupChallengesLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                <Title>Which entries would you like to update?</Title>
                <GroupedChallengesList
                  challengesData={challengesData}
                  disableRowClick
                  onCheckBoxClick={this.handleCheckBoxClick}
                  selectedIds={selectedIds}
                />
                <FlexButtonContainer>
                  <StyledButton size="medium" kind="secondary" link="/lobby">
                    {t('Lobby')}
                  </StyledButton>
                  <StyledButton
                    disabled={selectedIds.length === 0}
                    size="medium"
                    kind="primary"
                    link={
                      selectedIds.length === 0
                        ? ''
                        : `/challenge/${goToChallenge}/${selectedIds.join('-')}`
                    }
                  >
                    {t('Continue')}
                  </StyledButton>
                </FlexButtonContainer>
              </React.Fragment>
            )}
          </Modal>
        )}
        {(confirmDeleteLineupId || confirmDeleteLinups || isDeleting) && (
          <ConfirmModal
            isLoading={isDeleting}
            onCancel={this.handleCancelDelete}
            onSubmit={this.handleDelete}
            title={confirmDeleteText}
            label="Delete"
          />
        )}
      </div>
    );
  }
}

const connector = connect(
  (state, { selectedSelectionHash }) => {
    const { active } = myTeamsSelector(state, selectedSelectionHash);
    const lineups = active || [];

    return {
      challengesData: editLineUpSelector(state, selectedSelectionHash),
      isLineupChallengesLoading: state.myTeams.isLineupChallengesLoading,
      lineups,
      isDeleting: state.myTeams.isDeleting,
      selectedLineUp: lineups.find(lineup => lineup.selectionHash === selectedSelectionHash)
    };
  },
  {
    loadLineupChallenges: myTeamsActions.getLineupChallenges,
    deleteLineups: myTeamsActions.deleteLineups
  }
);

export default compose(
  withStateHandlers(
    {
      selectedSelectionHash: null,
      selectedIds: []
    },
    {
      setSelectionHash: () => selectedSelectionHash => ({ selectedSelectionHash }),
      setSelectedIds: () => selectedIds => ({ selectedIds })
    }
  ),
  connector
)(MyUpcomingTeamsContent);
